import React from "react";
import { PensionIncomeConfiguration } from "../../../../../../../Wrap/Constants/Enums/Domain/PensionIncomeConfiguration";
import { DrawdownAnnualIncome } from "../../../../../../../Wrap/Models/Domains/Drawdown/DrawdownAnnualIncome";
import { CalculateEstimatedPaye } from "../../../NewOneOffIncomePaymentWizard/Components/Shared/CalculateEstimatedPaye";
import { processTypeEnum } from "../../Resource/Contents";
import { calculateAnnualIncomeAmount, calculateAnnualIncomeAmountWithInitialValue } from "./Helper/IncomePaymentHelper";
import { MoneyHelper } from "../../../../../../Shared/Common/MoneyHelper";
import { getQueryParameters } from "../../../../../../Shared/Wizards/Entities/QueryParametersHelper";
import { IProcessComponentProps, ProcessComponent } from "../../../../../../Shared/Process/Process/ProcessComponent";
import { PensionBase } from "../../../../../../../Wrap/Models/Models/QuoteAndApplyBase/PensionBase";
import { RegularCrystallisationDetails } from "../../../../../../../Wrap/Models/Domains/Drawdown/RegularCrystallisationDetails";
import { PensionEstimatedPayQuery } from "../../../../../../../Wrap/Models/Domains/Tax/PensionEstimatedPayQuery";
import { DrawdownType } from "../../../../../../../Wrap/Constants/Enums/Drawdown/DrawdownType";
import { CurrencyCode } from "../../../../../../../Wrap/Constants/Enums/Domain/CurrencyCode";
import { JsonDateParser } from "../../../../../../../react-core/Utils/JsonDateParser";

interface IEstimatedPAYEProps extends IProcessComponentProps {
    data: DrawdownAnnualIncome;
    drawdownType: DrawdownType;
    regularCrystallisationDetails?: RegularCrystallisationDetails;
    pensionBase: PensionBase;
    processType?: processTypeEnum;
}
interface IEstimatedPAYEState {
}

export class EstimatedPAYE extends ProcessComponent<IEstimatedPAYEProps, IEstimatedPAYEState> {
    constructor(props: any) {
        super(props);
    }

    private getCalculateEstimatedPayeQueries():Array<PensionEstimatedPayQuery> {
        const { data, pensionBase, processType, regularCrystallisationDetails, drawdownType } = this.props;
        var frequency = this.getIncomeFrequency(drawdownType, regularCrystallisationDetails, data?.Frequency);
        if (data?.TargetSpecificArrangements && ![processTypeEnum.topUpWithITFLS, processTypeEnum.NewBusiness, processTypeEnum.Quote].includes(processType)) {
            var queryList: Array<PensionEstimatedPayQuery> = [];
            (pensionBase?.DrawdownIncomeAmendmentModel?.DrawdownIncomeAmendmentInstruction?.CappedArrangements || []).forEach(capped => {
                const nextPaymentDate = JsonDateParser.toDateNoOffset(data.NextPayment);
                const nextYearGadStartDate = JsonDateParser.toDateNoOffset(capped.NextGadStartDate);

                const endOfCurrentPensionYear = new Date(nextYearGadStartDate);

                nextPaymentDate.setHours(0, 0, 0);
                endOfCurrentPensionYear.setHours(0, 0, 0);

                const grossAmount = nextPaymentDate < endOfCurrentPensionYear ?
                    capped.IncomePerPaymentForCurrentPensionYear :
                    capped.IncomePerPaymentForFuturePensionYears;

                if (grossAmount > 0) {
                    queryList.push(
                        {
                            HierarchyId: getQueryParameters()["subaccounthierarchyid"],
                            PensionArrangementId: capped.PensionArrangementId,
                            PensionIncomeConfiguration: PensionIncomeConfiguration.ArrangementLevel,
                            GrossAmount: { Currency: MoneyHelper.getDefaultCurrency(), Amount: grossAmount },
                            Frequency: frequency,
                            PaymentDate: data.NextPayment
                        }
                    )
                }
            });

            (pensionBase?.DrawdownIncomeAmendmentModel?.DrawdownIncomeAmendmentInstruction?.FlexiArrangements ?? []).forEach(flexi => {
                if (flexi?.IncomePerPayment > 0) {
                    queryList.push(
                        {
                            HierarchyId: getQueryParameters()["subaccounthierarchyid"],
                            PensionArrangementId: flexi.PensionArrangementId,
                            PensionIncomeConfiguration: PensionIncomeConfiguration.ArrangementLevel,
                            GrossAmount: { Currency: MoneyHelper.getDefaultCurrency(), Amount: flexi.IncomePerPayment },
                            Frequency: frequency,
                            PaymentDate: data.NextPayment
                        }
                    )
                }
            });

            return queryList;
        }
        else {
            return [
                {
                    HierarchyId: getQueryParameters()["subaccounthierarchyid"],
                    PensionIncomeConfiguration: data?.TargetSpecificArrangements ? PensionIncomeConfiguration.ArrangementLevel : PensionIncomeConfiguration.PensionLevel,
                    GrossAmount: { Amount: this.getDrawdownAnnualIncome(data, frequency) + this.getTotalTransferInAnnualIncome(pensionBase, frequency), Currency: CurrencyCode.GBP },
                    Frequency: frequency,
                    PaymentDate: data.NextPayment
                }]
        }
    }

    private getDrawdownAnnualIncome(drawdownAnnualIncome, frequency) {
        var annualIncome = this.getAnnualAmountFromDrawdownIncome(this.props.processType, drawdownAnnualIncome, frequency)?.Amount ?? 0;

        return drawdownAnnualIncome.GrossInitialIncomePayment?.Amount > 0
            ? calculateAnnualIncomeAmountWithInitialValue(
                annualIncome,
                drawdownAnnualIncome.GrossInitialIncomePayment?.Amount ?? 0,
                frequency).Amount
            : annualIncome
    }

    private getTotalTransferInAnnualIncome(pensionBase: PensionBase, frequency) {
        if (pensionBase.PensionTransfers == null || !pensionBase.PensionTransfers.some(x => x.IncomePaymentDetails?.GrossIncomePerPayment?.Amount > 0)) {
            return 0;
        }
        const totalTransferInAmount =
            pensionBase.PensionTransfers
                .filter(x => x.IncomePaymentDetails?.GrossIncomePerPayment?.Amount > 0)
                .reduce((sum, transfer) => sum + calculateAnnualIncomeAmount(transfer.IncomePaymentDetails?.GrossIncomePerPayment?.Amount, frequency).Amount, 0);
        return totalTransferInAmount;
    }

    private getAnnualAmountFromDrawdownIncome(processType,data:DrawdownAnnualIncome,frequency){
        if ([processTypeEnum.NewBusiness, processTypeEnum.Quote].includes(processType)) {
            return calculateAnnualIncomeAmount(data.GrossIncomePerPayment?.Amount, frequency);
        }

        return data.AnnualAmount?.Amount
    }

    private getIncomeFrequency(drawdownType, regularCrystallisationDetails, frequency) {
        return drawdownType === DrawdownType.Regular ? regularCrystallisationDetails?.Frequency : frequency
    }

    public render(): JSX.Element {
        return (
            <>
                <h3>Estimated PAYE</h3>
                <CalculateEstimatedPaye query={this.getCalculateEstimatedPayeQueries()} />
            </>
        );
    }
}
